import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { IStore } from "@/redux/defaultStore";
import { useSelector } from "react-redux";
import { FC, ComponentProps, Suspense, lazy, useEffect, useRef, useState } from "react";
import DevourAccountDropdownMenuBody from "./DevourAccountDropdownMenuBody";
import useClosePopups from "@/hooks/useClosePopups";
import { isPhantomApp } from "@/utils/isGoNative";
import { createPortal } from "react-dom";
import XpMultiplierTooltip from "@/components/XpMultiplierTooltip.tsx";
import { useNavigate, useLocation } from "react-router";
import OverwolfGameStatusChip from "@/pages/overwolf/components/OverwolfGameStatusChip";
import getConfig from "@/utils/getConfig";
import {OverwolfApi} from "@devour/client";
import { DEFAULT_AVATAR } from "@/utils/resolveUserAvatar";
import UserAvatar from "@/components/UserAvatar";
import useFilteredOverwolfGameInfo from "@/hooks/useFilteredOverwolfGameInfo";
/*
 * Lazy + only rendered when the wallet dropdown is open, so its thirdweb
 * (WalletConnect registry) never loads on the global top nav.
 */
const DevourWalletDropdownMenuBody = lazy(() => import("./DevourWalletDropdownMenuBody"));

/*
 * XP ring geometry. r=17 inside a 40 viewBox leaves room for the 2px stroke
 * without clipping; the circumference is precomputed so the dash array is a
 * multiply rather than a magic number.
 */

interface props {
    onShowSignInToast: () => void;
    hideWalletOnMobile?: boolean;
    dropdownAnchorElement?: string;
    avatarOnly?: boolean;
    showGameStatus?: boolean;
    gameStatusCompact?: boolean;
    /*
     * Render the status chip as JUST the colored dot — no text. Used in the
     * in-game compact panel (~380px wide) where the full or even
     * abbreviated chip pushes the avatar off-screen.
     */
    gameStatusIconOnly?: boolean;
    customAvatarUrl?: string;
    hideChallengeSiteMenuItems?: boolean;
    /** Optional callback to override default invite modal behavior */
    onInviteClick?: () => void;
    /** Hide XP multiplier tooltip (for branded challenge pages) */
    hideMultiplier?: boolean;
    /** Callback to open the rewards drawer from the dropdown menu */
    onOpenRewardsDrawer?: () => void;
    /** Hide the user's profile image (shows generic icon instead) */
    hideUserImage?: boolean;
    /* Passed straight through to the dropdown — see its Props for what it carries. */
    playerPanel?: ComponentProps<typeof DevourAccountDropdownMenuBody>["playerPanel"];
    /** Hide the "Download DevourPlay" row in the dropdown — for partner-isolated brand hubs */
}

const DevourTopNavUserData: FC<props> = ({ onShowSignInToast, dropdownAnchorElement, showGameStatus = false, gameStatusCompact = false, gameStatusIconOnly = false, customAvatarUrl, hideChallengeSiteMenuItems = false, onInviteClick, hideMultiplier = false, onOpenRewardsDrawer, hideUserImage = false, playerPanel }) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: userProfileData} = useGetUserProfile(fullToken);
    const accountDropdownRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const walletDropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    /*
     * Use the filtered gameInfo so the top-nav chip and navigation handler don't
     * react to launcher flicker (Battle.net taking over when Ricochet revokes COD's
     * GEP hook). See useFilteredOverwolfGameInfo for suppression + debounce rules.
     */
    const gameInfo = useFilteredOverwolfGameInfo();
    const [
        showAccountDropdown,
        setShowAccountDropdown,
    ] = useState<boolean>(false);
    const [
        showWalletDropdown,
        setShowWalletDropdown,
    ] = useState<boolean>(false);

    const getParentAnchor = () => {
        if (dropdownAnchorElement) {
            return document.querySelector(dropdownAnchorElement) ?? document.body;
        }
        return document.body;

    };

    function closeDropdowns(event: MouseEvent, isModalActive: number) {

        if (!isModalActive) {
            if (walletDropdownRef.current && !walletDropdownRef.current.contains(event.target as Node)) {
                setShowWalletDropdown(false);
            }

            if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
                setShowAccountDropdown(false);
            }
        }
    }

    function handleToggleWalletDropdown(): void {

        if (!fullToken) {
            onShowSignInToast();
        } else {
            setShowWalletDropdown((prev) => !prev);
            if (showAccountDropdown) {
                setShowAccountDropdown(false);
            }
        }
    }

    function handleToggleAccountDropdown(): void {
        if (!fullToken) {
            onShowSignInToast();
        } else {
            setShowAccountDropdown((prev) => !prev);
            if (showWalletDropdown) {
                setShowWalletDropdown(false);
            }
        }
    }

    function handleStopPropagation(e: React.MouseEvent | React.TouchEvent): void {
        e.stopPropagation();
    }

    async function handleGameStatusChipClick(): Promise<void> {
        if (!gameInfo?.isGameRunning || !gameInfo.gameId) return;

        const api = new OverwolfApi(getConfig());
        const data = await api.getOverwolfEnhancedGamesSearch({gameId: String(gameInfo.gameId), limit: 1}) as any;
        const game = data?.data?.[0];

        if (game?.slug) {
            navigate(`/games/${game.slug}`);
        } else if (game?.name) {
            const fallbackSlug = game.name.toLowerCase().replace(/\s+/g, "-");
            navigate(`/games/${fallbackSlug}`);
        }
    }

    useEffect(() => {
        if (isPhantomApp()) {
            setShowWalletDropdown(true);
        }
    }, []);
    useClosePopups(closeDropdowns);

    const renderProfilePicture = () => {
        if (hideUserImage) {
            const tag = userProfileData?.user?.nickname || userProfileData?.user?.firstName || "";
            return (
                <div className="devour-top-nav_navbar_user-data_tag">
                    {tag}
                </div>
            );
        }

        return (
            <div className="devour-top-nav_navbar_user-data_image-container">
                {fullToken
                    ? <UserAvatar
                        user={userProfileData?.user}
                        size="fill"
                        /* Branded page override (e.g. the Polaris COD logo) stands in
                           when the user has no avatar of their own. */
                        fallback={customAvatarUrl || DEFAULT_AVATAR}
                        alt="User Profile"
                    />
                    : <img
                        src={customAvatarUrl || `${import.meta.env.VITE_CDN_URL}/images/FUEL.webp`}
                        alt="User Profile"
                    />
                }
            </div>
        );

    };

    // Hide multiplier on merchant pages to prevent overflow on mobile
    const isMerchantPage = location.pathname.startsWith("/merchants");

    return (
        <div className="devour-top-nav_navbar_user-data" style={{ marginLeft: "auto" }}>
            {/* XpMultiplierTooltip removed — DevourTopNav now renders the broadcast-style
                multiplier chip directly in the stats strip. Two chips were appearing. */}

            {/* Temporarily commented out My Wallets section
            {getCanShowWallet() && <div
                className="devour-top-nav_navbar_user-data_wallets"
                onClick={handleToggleWalletDropdown}
                onMouseDown={handleStopPropagation}
                onTouchStart={handleStopPropagation}
            >
                {(isDesktop || isTablet) && <p>My Wallets</p>}
                <IoWalletSharp/>
                <p>{userProfileData?.wallets?.length
                    ? userProfileData?.wallets?.length
                    : 0}</p>
            </div>}
            */}

            {/* Game status chip - positioned before nav icons */}
            {showGameStatus &&
                <div className="devour-top-nav_navbar_user-data_game-status">
                    <OverwolfGameStatusChip
                        compact={gameStatusCompact}
                        iconOnly={gameStatusIconOnly}
                        onClick={handleGameStatusChipClick}
                    />
                </div>
            }

            {/*
              * The avatar is the TIER glance: UserAvatar draws the ring in
              * the tier's admin-assigned colour. XP and level live on the
              * bar's LVL chip — nothing said twice.
              */}
            <div
                className="devour-top-nav_navbar_user-data_image-button"
                onClick={handleToggleAccountDropdown}
                onMouseDown={handleStopPropagation}
                onTouchStart={handleStopPropagation}
            >
                {renderProfilePicture()}
            </div>

            {fullToken &&
            (createPortal(<>
                <div
                    className="devour-top-nav_dropdown devour-top-nav_dropdown_wallet"
                    ref={walletDropdownRef}
                    style={{
                        opacity: !showWalletDropdown
                            ? "0"
                            : "1",
                        transition: "all .2s",
                        visibility: !showWalletDropdown
                            ? "hidden"
                            : "visible",
                    }}
                >
                    {showWalletDropdown &&
                        <Suspense fallback={null}>
                            <DevourWalletDropdownMenuBody/>
                        </Suspense>
                    }
                </div>

                <div
                    className="devour-top-nav_dropdown devour-top-nav_dropdown_account"
                    ref={accountDropdownRef}
                    style={{
                        opacity: !showAccountDropdown
                            ? "0"
                            : "1",
                        transition: "all .2s",
                        visibility: !showAccountDropdown
                            ? "hidden"
                            : "visible",
                    }}
                >
                    <DevourAccountDropdownMenuBody
                        renderProfilePicture={renderProfilePicture}
                        toggleWalletDropdown={handleToggleWalletDropdown}
                        hideChallengeSiteMenuItems={hideChallengeSiteMenuItems}
                        onInviteClick={onInviteClick}
                        onOpenRewardsDrawer={onOpenRewardsDrawer}
                        playerPanel={playerPanel}
                    />
                </div>
            </>, getParentAnchor()) as React.ReactNode)
            }
        </div>
    );

};

export default DevourTopNavUserData;